import { EventNames, isMobile, ScrollLock } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import { PictureProfile } from '../../../ResizedImage/PictureProfile.csharp';
import { getTranslation, keys } from '../../../Resources/Translations';
import { Input } from '../../Components/Common/Input';
import { SearchResultViewModel } from '../../Filtering/Api/SearchResultViewModel.csharp';
import { getFor } from '../../ReactComponents/API';
import { RecommendationsService } from '../../Recommendations/ViewModels/RecommendationsService';
import { RecommendedProductsProps } from '../../Recommendations/ViewModels/RecommendedProductsProps.csharp';
import { GoogleTagManager } from '../../Tracking/GoogleTagManager/GoogleTagManager';
import { HeaderVariant } from '../Header/HeaderVariant.csharp';
import { SearchSuggestions } from './SearchSuggestions';

export interface SearchProps {
    searchUrl: string;
    searchApiUrl: string;
    searchPhrase: string;
    variant: HeaderVariant;
    isOpen: boolean;
    isActive: boolean;
    isSticky: boolean;
    pictureProfile: PictureProfile;
    setSearchActive: (value: boolean) => void;
    setOverlayShown: (value: boolean) => void;
    searchPlaceholder: string;
    epiFindClickTrackingUrl: string;
    experimentProductClickUrl: string;
    leadInText: string;
    searchRecommendationsProps?: RecommendedProductsProps;
}

export const Search = React.forwardRef((props: SearchProps, ref: React.Ref<HTMLDivElement>) => {
    const {
        variant,
        searchApiUrl,
        searchPhrase,
        searchUrl,
        epiFindClickTrackingUrl,
        experimentProductClickUrl,
        pictureProfile,
        isOpen,
        isSticky,
        setSearchActive,
        isActive,
        searchPlaceholder,
        searchRecommendationsProps
    } = props;

    const placeholderValue = searchPlaceholder || getTranslation(keys['header.searchPlaceholder']);
    const [value, setValue] = useState(searchPhrase ?? '');
    const [phrase, setPhrase] = useState(searchPhrase ?? '');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isAboutToRedirect, setIsAboutToRedirect] = useState(false);
    const [searchResult, setSearchResult] = useState({} as SearchResultViewModel);
    const [response, setResponse] = useState<{ result: SearchResultViewModel; value: string } | undefined>(undefined);
    const [isClient, setIsClient] = useState(false);

    const [isSearchOpen, setIsSearchOpen] = React.useState<boolean>(isOpen);

    useEffect(() => {
        setIsClient(true);
        window.addEventListener('popstate', handleNavigationEvent);
        window.addEventListener(EventNames.ResetSearch, resetSearch);

        return () => {
            window.removeEventListener('popstate', handleNavigationEvent);
            window.removeEventListener(EventNames.ResetSearch, resetSearch);
        };
    }, []);

    useEffect(() => {
        setIsSearchOpen(isOpen);

        if (isOpen && !isActive && variant !== HeaderVariant.Search) {
            closeSearch();
        }
    }, [isOpen, isActive]);

    useEffect(() => {
        if (value && response) {
            setSearchResult(response.result);
            if (searchRecommendationsProps != null) {
                RecommendationsService.RefreshRecommendationsForSearch(
                    value,
                    response.result.products.map((p) => p.code),
                    response.result.totalCount
                );
            }
            if (!isSearchResultEmpty(response.result)) setShowSuggestions(true);
        }
    }, [response]);

    useEffect(() => {
        props.setOverlayShown(showSuggestions || isLoading);
    }, [value, showSuggestions, searchResult, isSearchOpen, isLoading]);

    const getInputElement = (): HTMLInputElement | null => {
        const outsideRef = ref as React.RefObject<HTMLDivElement>;

        if (outsideRef.current) {
            const inputs = outsideRef.current.getElementsByTagName('input');
            return inputs[0];
        }

        return null;
    };

    const handleNavigationEvent = () => {
        const searchParams = new URLSearchParams(window.location.search);
        setValue(searchParams.get('searchPhrase') || '');
    };

    const isSearchResultEmpty = (searchResult: SearchResultViewModel | null) =>
        !searchResult && (searchResult!.products || !searchResult!.categoriesLinks || !searchResult!.brandsLinks);

    const refreshList = (value: string) => {
        const event = new CustomEvent(EventNames.Search, {
            detail: { phrase: value }
        });
        window.dispatchEvent(event);
    };

    const getSearchPhraseUrl = (url: string, withoutSlash?: boolean, searchValue?: string): string => {
        return `${withoutSlash ? '' : '/'}${url}?searchPhrase=${encodeURIComponent(searchValue ?? value)}`;
    };

    const search = (value: string) => {
        if (!value) return;

        setSearchActive(true);
        if (variant == HeaderVariant.Search) {
            refreshList(value);
            return;
        }

        if (typeof window !== 'undefined') {
            setIsAboutToRedirect(true);
            window.location.href = getSearchPhraseUrl(searchUrl);
        }
    };

    const resetSearch = () => {
        setPhrase('');
        setValue('');
        if (!isMobile()) setShowSuggestions(false);
        clearSuggestions();
        setIsLoading(false);
    };

    const clearSuggestions = () => {
        setSearchResult({} as SearchResultViewModel);
    };

    const searchSuggestions = (value: string) => {
        if (isAboutToRedirect) {
            return;
        }

        if (variant == HeaderVariant.Search) {
            refreshList(value);
            return;
        }

        setIsLoading(true);
        getFor<SearchResultViewModel>(getSearchPhraseUrl(searchApiUrl, true))
            .then((result) => {
                setResponse({ result, value });
            })
            .catch(() => {
                setSearchResult({
                    products: [],
                    categoriesLinks: [],
                    brandsLinks: [],
                    articlesLinks: [],
                    suggestionPhrases: [],
                    totalCount: 0
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const clearSearch = () => {
        const input = getInputElement();
        if (input) input.focus();

        if (variant == HeaderVariant.Search) {
            refreshList('');
        }

        resetSearch();
    };

    const closeSearch = () => {
        setSearchActive(false);
        setShowSuggestions(false);

        if (variant != HeaderVariant.Search) {
            resetSearch();
        }
    };

    const onAfterChange = () => {
        if (phrase === value || value === '') return;
        setPhrase(value);
        searchSuggestions(value);
        if (variant != HeaderVariant.Search) {
            GoogleTagManager.searchSuggestion(value);
        }
    };

    const changeSearchActiveStatus = (value: boolean) => {
        setSearchActive(value);
    };

    return (
        <div
            className={classNames(
                'RichSearch',
                isSearchOpen && 'RichSearch--Open',
                isActive && 'RichSearch--Active',
                variant !== HeaderVariant.Search && 'RichSearch--WithoutAnimation'
            )}
            key={isClient ? 'client' : 'server'}
        >
            <ScrollLock isLocked={showSuggestions || isLoading} />
            <div className="RichSearch__Inner">
                <div className="RichSearch__InputWrapper" ref={ref}>
                    <label className="visuallyHidden" htmlFor="RichSearch__Input">
                        {getTranslation(keys['header.searchLabel'])}
                    </label>
                    <Input
                        type="text"
                        className="RichSearch__Input"
                        value={value}
                        id="RichSearch__Input"
                        onChange={(value) => {
                            setValue(value);
                            setSearchActive(true);
                        }}
                        onChangeCondition={(value: string) => {
                            return value !== undefined && value.search('[<>]') === -1;
                        }}
                        onClick={() => changeSearchActiveStatus(true)}
                        onKeyDown={() => search(value)}
                        onCancel={closeSearch}
                        placeholder={placeholderValue}
                        onAfterChange={onAfterChange}
                        tabIndex={isSearchOpen ? undefined : -1}
                        autocomplete="off"
                    />
                    <button className="RichSearch__CloseButton" onClick={closeSearch} type="button">
                        <span className="visuallyHidden">{getTranslation(keys['navigation.close'])}</span>
                    </button>
                    <div className="RichSearch__ButtonsRight">
                        <button
                            className={classNames('RichSearch__ClearButton', phrase && 'RichSearch__ClearButton--Visible')}
                            onClick={clearSearch}
                            type="button"
                        >
                            <span className="visuallyHidden">{getTranslation(keys['header.clearSearch'])}</span>
                        </button>
                        <div className="RichSearch__ClearButtonDivider" />
                        <button
                            aria-disabled={value.length === 0}
                            className={classNames('RichSearch__SearchButton', { 'RichSearch__SearchButton--Active': !!value })}
                            onClick={() => search(value)}
                            type="button"
                        >
                            <span className="visuallyHidden">{getTranslation(keys['header.searchPlaceholder'])}</span>
                        </button>
                    </div>
                </div>
            </div>
            {isOpen && (showSuggestions || isLoading) && (
                <SearchSuggestions
                    isLoading={isLoading}
                    isSticky={isSticky}
                    searchPhrase={phrase}
                    searchResult={searchResult}
                    lastSearchPhrase={response?.value}
                    searchUrl={searchUrl}
                    pictureProfile={pictureProfile}
                    getSearchPhraseUrl={getSearchPhraseUrl}
                    epiFindClickTrackingUrl={epiFindClickTrackingUrl}
                    experimentProductClickUrl={experimentProductClickUrl}
                    closeSearch={closeSearch}
                    unfocusInput={() => {
                        const input = getInputElement();
                        input && input.blur();
                    }}
                    searchRecommendationsProps={searchRecommendationsProps}
                />
            )}
        </div>
    );
});
